import React, { forwardRef, useEffect, useState } from 'react';
import { getDefaultBankAccount } from '../api';
import { CreditCard, Banknote, Building2, Smartphone, Wallet } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const FALLBACK_BANK = {
  name: 'Wio Bank',
  iban: 'AE15 0860 0000 0971 6265 342',
  ibanCompact: 'AE150860000009716265342',
  bic: 'WIOBAEADXXX',
  address: 'Etihad Airways Centre, 5th Floor, Abu Dhabi, UAE',
  beneficiary: 'iAccounting For Business Manager LLC CO'
};
const toBank = (a) => (a ? { name: a.bank_name, iban: a.iban, ibanCompact: a.iban_compact || (a.iban || '').replace(/\s/g, ''), bic: a.bic, address: a.address, beneficiary: a.beneficiary, accountNumber: a.account_number } : null);
let cachedDefault = null;

const methodLabels = {
  bank: { en: 'Bank Transfer', ar: 'تحويل بنكي', icon: Building2, color: '#1F72CD' },
  cash: { en: 'Cash', ar: 'نقدي', icon: Banknote, color: '#3b82f6' },
  apple_pay: { en: 'Apple Pay', ar: 'Apple Pay', icon: Smartphone, color: '#000' },
  google_pay: { en: 'Google Pay', ar: 'Google Pay', icon: Smartphone, color: '#4285F4' },
  card: { en: 'Card', ar: 'بطاقة', icon: CreditCard, color: '#7C3AED' },
  cheque: { en: 'Cheque', ar: 'شيك', icon: Wallet, color: '#EA580C' }
};

const ReceiptView = forwardRef(({ receipt, lang = 'en' }, ref) => {
  const [defaultBank, setDefaultBank] = useState(cachedDefault);
  useEffect(() => {
    if (cachedDefault || receipt?.bank_account) return;
    getDefaultBankAccount().then((a) => { cachedDefault = toBank(a); setDefaultBank(cachedDefault); }).catch(() => {});
  }, [receipt]);
  if (!receipt) return null;
  const BANK = toBank(receipt.bank_account) || defaultBank || FALLBACK_BANK;
  const method = methodLabels[receipt.payment_method] || methodLabels.cash;
  const MIcon = method.icon;
  const issued = new Date(receipt.issued_at);
  const dateStr = issued.toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB', { year: 'numeric', month: 'long', day: '2-digit' });
  const timeStr = issued.toLocaleTimeString(lang === 'ar' ? 'ar-AE' : 'en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <div ref={ref} dir={lang === 'ar' ? 'rtl' : 'ltr'} className="receipt-printable bg-white text-[#0a0f1c] p-10 max-w-3xl mx-auto shadow-2xl print:shadow-none" style={{ fontFamily: lang === 'ar' ? "'Cairo', 'Tajawal', sans-serif" : "'Inter', sans-serif" }}>
      {/* Header */}
      <div className="flex items-start justify-between pb-6 border-b-2 border-[#c9a45c]">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e6c78a 0%, #a8823c 100%)' }}>
              <span className="text-white font-bold text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>i</span>
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0a0f1c' }}>iAccounting</div>
              <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f]">Company Consultation</div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600 leading-relaxed">
            Business Bay, Dubai, UAE<br/>
            info@iaccounting.ae &middot; +971 55 109 6418
          </div>
        </div>
        <div className="text-end" dir="ltr">
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f]">{lang === 'ar' ? 'إيصال دفع' : 'Payment Receipt'}</div>
          <div className="text-lg font-mono font-bold text-[#a8823c] mt-1">{receipt.id}</div>
          <div className="text-xs text-gray-600 mt-2">{dateStr}</div>
          <div className="text-[10px] text-gray-500">{timeStr} GST</div>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${receipt.status === 'paid' ? 'bg-blue-100 text-blue-700' : receipt.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
            {receipt.status === 'paid' ? (lang === 'ar' ? 'مدفوع' : 'PAID') : receipt.status === 'pending' ? (lang === 'ar' ? 'معلق' : 'PENDING') : (lang === 'ar' ? 'مسترد' : 'REFUNDED')}
          </span>
        </div>
      </div>

      {/* Bill To */}
      <div className="grid grid-cols-2 gap-6 mt-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] mb-2">{lang === 'ar' ? 'الفوترة إلى' : 'Bill To'}</div>
          <div className="font-semibold text-base">{receipt.customer_name}</div>
          {receipt.customer_email && <div className="text-sm text-gray-600">{receipt.customer_email}</div>}
          {receipt.customer_phone && <div className="text-sm text-gray-600">{receipt.customer_phone}</div>}
          {receipt.application_id && (
            <div className="mt-2 text-xs text-gray-500">
              {lang === 'ar' ? 'مرجع الطلب' : 'Ref Application'}: <span className="font-mono text-[#a8823c]">{receipt.application_id}</span>
            </div>
          )}
        </div>
        <div className="text-end">
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] mb-2">{lang === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: method.color, color: method.color }}>
            <MIcon className="w-4 h-4" />
            <span className="font-medium text-sm">{method[lang]}</span>
          </div>
          {receipt.payment_reference && (
            <div className="mt-2 text-xs text-gray-600">
              {lang === 'ar' ? 'مرجع' : 'Ref'}: <span className="font-mono">{receipt.payment_reference}</span>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {receipt.description && (
        <div className="mt-6 p-4 bg-[#faf6ec] border-l-4 border-[#c9a45c] rounded text-sm">
          {receipt.description}
        </div>
      )}

      {/* Items table */}
      <table className="w-full mt-6 border-collapse">
        <thead>
          <tr className="border-b-2 border-[#c9a45c]">
            <th className="text-start py-3 text-[10px] uppercase tracking-widest text-[#8b6f2f] font-semibold">{lang === 'ar' ? 'الوصف' : 'Description'}</th>
            <th className="text-center py-3 text-[10px] uppercase tracking-widest text-[#8b6f2f] font-semibold w-20">{lang === 'ar' ? 'الكمية' : 'Qty'}</th>
            <th className="text-end py-3 text-[10px] uppercase tracking-widest text-[#8b6f2f] font-semibold w-28">{lang === 'ar' ? 'السعر' : 'Unit Price'}</th>
            <th className="text-end py-3 text-[10px] uppercase tracking-widest text-[#8b6f2f] font-semibold w-32">{lang === 'ar' ? 'الإجمالي' : 'Amount'}</th>
          </tr>
        </thead>
        <tbody>
          {receipt.items.map((it, i) => (
            <tr key={i} className="border-b border-gray-200">
              <td className="py-3 text-sm">{it.description}</td>
              <td className="py-3 text-sm text-center">{it.quantity}</td>
              <td className="py-3 text-sm text-end tabular-nums">{Number(it.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              <td className="py-3 text-sm text-end tabular-nums font-medium">{Number(it.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mt-6">
        <div className="w-72 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{lang === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
            <span className="tabular-nums">{Number(receipt.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })} {receipt.currency}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{lang === 'ar' ? (receipt.vat_inclusive !== false ? 'من ضمنها ضريبة القيمة المضافة' : 'ضريبة القيمة المضافة') : (receipt.vat_inclusive !== false ? 'VAT included' : 'VAT')} ({receipt.vat_percent}%)</span>
            <span className="tabular-nums">{Number(receipt.vat_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} {receipt.currency}</span>
          </div>
          <div className="flex justify-between pt-3 border-t-2 border-[#c9a45c] text-lg font-bold">
            <span>{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
            <span className="tabular-nums text-[#a8823c]">{Number(receipt.total).toLocaleString(undefined, { minimumFractionDigits: 2 })} {receipt.currency}</span>
          </div>
        </div>
      </div>

      {/* Notes & Footer */}
      {receipt.notes && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] mb-1">{lang === 'ar' ? 'ملاحظات' : 'Notes'}</div>
          <div className="text-xs text-gray-700 whitespace-pre-wrap">{receipt.notes}</div>
        </div>
      )}

      {/* Bank details */}
      <div className="mt-6 rounded-lg border border-[#c9a45c]/40 bg-[#faf6ec] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] font-semibold mb-2">
          {lang === 'ar' ? 'تفاصيل التحويل البنكي' : 'Bank Transfer Details'}
        </div>
        <div className="flex items-start gap-4">
          <div className="flex-1 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-700" dir="ltr">
            <div><span className="text-[#8b6f2f] font-semibold">{lang === 'ar' ? 'اسم البنك' : 'Bank Name'}:</span> {BANK.name}</div>
            <div><span className="text-[#8b6f2f] font-semibold">BIC / SWIFT:</span> {BANK.bic}{BANK.accountNumber ? <span className="ms-3"><span className="text-[#8b6f2f] font-semibold">{lang === 'ar' ? 'رقم الحساب' : 'Acc. No'}:</span> <span className="font-mono">{BANK.accountNumber}</span></span> : null}</div>
            <div className="col-span-2"><span className="text-[#8b6f2f] font-semibold">IBAN:</span> <span className="font-mono">{BANK.iban}</span></div>
            <div className="col-span-2"><span className="text-[#8b6f2f] font-semibold">{lang === 'ar' ? 'العنوان' : 'Address'}:</span> {BANK.address}</div>
            <div className="col-span-2"><span className="text-[#8b6f2f] font-semibold">{lang === 'ar' ? 'المستفيد' : 'Beneficiary'}:</span> {BANK.beneficiary}</div>
          </div>
          <div className="flex flex-col items-center gap-1 shrink-0" dir="ltr">
            <div className="p-1.5 bg-white rounded border border-[#c9a45c]/40">
              <QRCodeSVG
                value={`BEN:${BANK.beneficiary}\nBANK:${BANK.name}\nIBAN:${BANK.ibanCompact}\nBIC:${BANK.bic}`}
                size={92}
                level="M"
                fgColor="#0a0f1c"
                bgColor="#ffffff"
              />
            </div>
            <div className="text-[9px] uppercase tracking-widest text-[#8b6f2f] font-semibold">{lang === 'ar' ? 'مسح للدفع' : 'Scan to Pay'}</div>
          </div>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-gray-200 flex justify-between items-end text-xs text-gray-500">
        <div>
          <div>{lang === 'ar' ? 'أصدرها' : 'Issued By'}: <span className="text-gray-700">{receipt.issued_by_name}</span></div>
          <div className="mt-1">{lang === 'ar' ? 'شكراً لتعاملكم معنا' : 'Thank you for your business'}</div>
        </div>
        <div className="text-end">
          <div className="w-40 border-t border-gray-400 mt-8 pt-1">{lang === 'ar' ? 'توقيع وختم' : 'Signature & Stamp'}</div>
        </div>
      </div>
    </div>
  );
});

ReceiptView.displayName = 'ReceiptView';
export default ReceiptView;
